import { useCallback, useEffect, useState } from 'react'
import { api, type EinrichtungBild, type EinrichtungHinweis,
         type EinrichtungArtikel, type EinrichtungStation,
         type EinrichtungRabattart, type EinrichtungZahlart } from '../api'
import { anzahl, datumLang, euro, prozent } from '../format'
import { Karte } from '../komponenten/Karte'
import { Tipp } from '../komponenten/Hinweis'

/* Was das System über die Kasse dieses Kunden weiß.
 *
 * Diese Ansicht stellt keine Fragen, sie berichtet. Der Unterschied ist
 * nicht kosmetisch: Eine Einrichtung, die dreissig Angaben abfragt,
 * wird nicht gemacht. Eine, die dreissig aus den Stammdaten liest und
 * drei zur Bestätigung vorlegt, wird gemacht.
 */

/**
 * Die Zahlarten der Kasse — jede, nicht nur die unklaren.
 *
 * Der Grund ist der teuerste Fall, nicht der häufigste. Eine Zahlart,
 * die das System **falsch** einordnet, weicht in keiner einzigen Zahl
 * ab: Die Summe stimmt, der Abgleich geht auf, die Barquote stimmt.
 * Sichtbar wird es allein hier — wenn jemand „Geschenkkarte — Karte"
 * nebeneinander liest und sagt: nein, das ist ein Gutschein.
 *
 * Deshalb steht neben unserer Art der **rohe Typ der Kasse**. Ohne ihn
 * wäre die Zeile eine Behauptung ohne Beleg.
 *
 * **Bar lässt sich nicht ändern.** Der Kassenabschluss weist die Summe
 * der Barzahlungen selbst aus, und der Abgleich hält sie dagegen
 * (`SUM_CASH`). Eine Prüfung, die von einer Einstellung abhängt, prüft
 * nichts.
 */
function Zahlartenkarte({ zeilen, neuLaden }: {
  zeilen: EinrichtungZahlart[]
  neuLaden: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)

  const setzen = (name: string, art: string) => {
    setFehler(null); setLaeuft(name)
    api.zahlartArtSetzen(name, art)
      .then(neuLaden)
      .catch((e) => setFehler(e.message))
      .finally(() => setLaeuft(null))
  }

  const zusammenfassen = (name: string, ziel: string) => {
    setFehler(null); setLaeuft(name)
    api.zahlartZusammenfassen(name, ziel || null)
      .then(neuLaden)
      .catch((e) => setFehler(e.message))
      .finally(() => setLaeuft(null))
  }

  const istZiel = new Set(zeilen.map((z) => z.anzeige_name).filter(Boolean))

  if (!zeilen.length) {
    return (
      <Karte titel="Zahlarten">
        <div className="leer">
          Noch keine Zahlarten aus Kassenarchiv oder Zahlungsbericht eingelesen.
        </div>
      </Karte>
    )
  }

  return (
    <Karte titel="Zahlarten"
           neben={`${anzahl(zeilen.length)} in der Kasse`}>
      {fehler && <div className="fehler" style={{ marginBottom: 10 }}>{fehler}</div>}
      <table>
        <thead>
          <tr>
            <th>Name in der Kasse</th>
            <th>Typ der Kasse</th>
            <th>Art</th>
            <th>Einordnung</th>
            <th>Zusammenfassen mit</th>
            <th className="r">Zuletzt gebucht</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.name}>
              <td>{z.name}</td>
              <td className="dezent">
                {z.typ ?? (
                  <Tipp text="Der Zahlungsbericht liefert keinen Typ. Auch ältere Archivbuchungen können ohne Typ vorliegen. Die Art können Sie hier festlegen." fokus>
                    <span>noch nicht erfasst</span>
                  </Tipp>
                )}
                {z.typ_unbekannt && (
                  <span className="merker" style={{ marginLeft: 6 }}>
                    unbekannt
                  </span>
                )}
              </td>
              <td>
                {z.art === 'CASH' ? (
                  <Tipp text="Bargeld weist der Kassenabschluss selbst aus und der Abgleich prüft es dagegen — deshalb nicht änderbar." fokus>
                    <span>Bar</span>
                  </Tipp>
                ) : (
                  <Tipp text={z.anzeige_name !== null || istZiel.has(z.name)
                    ? 'Zum Ändern der Art zuerst die Zusammenfassung lösen.'
                    : 'Art dieser Zahlart'}>
                    <select value={z.art}
                            disabled={laeuft === z.name
                              || z.anzeige_name !== null || istZiel.has(z.name)}
                            onChange={(e) => setzen(z.name, e.target.value)}>
                      <option value="CARD">Karte</option>
                      <option value="MEAL">Essensgutschein</option>
                      <option value="VOUCHER">Gutschein</option>
                      <option value="OTHER">Sonstiges</option>
                      <option value="DEBTOR">Auf Rechnung</option>
                    </select>
                  </Tipp>
                )}
                {z.art_quelle === 'KUNDE' && (
                  <Tipp text="Von Ihnen festgelegt. Der Import fasst das nicht mehr an." fokus>
                    <span className="dezent" style={{ marginLeft: 6 }}>festgelegt</span>
                  </Tipp>
                )}
              </td>
              <td>{z.nicht_eingeordnet ? 'noch nicht eingeordnet' : '—'}</td>
              <td>
                {z.art === 'CASH' ? (
                  <span className="dezent">nicht möglich</span>
                ) : (
                  <Tipp text={istZiel.has(z.name)
                    ? 'Diese Zahlart ist bereits Ziel einer Zusammenfassung.'
                    : 'Nur Zahlarten derselben Art stehen zur Wahl.'}>
                    <select value={z.anzeige_name ?? ''}
                            disabled={laeuft === z.name || istZiel.has(z.name)}
                            onChange={(e) => zusammenfassen(z.name, e.target.value)}>
                      <option value="">eigener Name</option>
                      {zeilen.filter((ziel) =>
                        ziel.name !== z.name && ziel.art === z.art
                        && ziel.anzeige_name === null).map((ziel) => (
                          <option key={ziel.name} value={ziel.name}>{ziel.name}</option>
                      ))}
                    </select>
                  </Tipp>
                )}
              </td>
              <td className="r zahl dezent">{datumLang(z.last_seen)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="dezent" style={{ marginTop: 10 }}>
        <b>Karte</b> fasst zusammen, was die Kasse getrennt benennt: „Tap
        to Pay" läuft im Buchungsgerät selbst, „Lightspeed Payments" geht
        an ein EC-Terminal — bezahlt wird beide Male mit einer Karte. Die
        Namen bleiben in der Tagesansicht eine Ebene tiefer erhalten.
        Was Sie hier festlegen, überschreibt der nächste Import nicht.
        Bitte prüfen Sie die Zahlarten mit „noch nicht eingeordnet“ und wählen Sie ihre Art.
      </p>
    </Karte>
  )
}

function Rabattartenkarte({ zeilen, neuLaden }: {
  zeilen: EinrichtungRabattart[]
  neuLaden: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)

  const bestaetigen = (name: string) => {
    setFehler(null); setLaeuft(name)
    api.rabattartFreigeben(name)
      .then(neuLaden)
      .catch((e) => setFehler(e.message))
      .finally(() => setLaeuft(null))
  }

  if (!zeilen.length) {
    return (
      <Karte titel="Rabattarten">
        <div className="leer">
          Noch kein Kassenarchiv eingelesen — die Rabattarten stehen darin.
        </div>
      </Karte>
    )
  }

  return (
    <Karte titel="Rabattarten" neben={`${anzahl(zeilen.length)} in der Kasse`}>
      {fehler && <div className="fehler" style={{ marginBottom: 10 }}>{fehler}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Name in der Kasse</th>
              <th>Stand</th>
              <th className="r">Erster geladener Tag</th>
              <th className="r">Zuletzt geladen</th>
              <th>Geprüft</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.name}>
                <td>{z.name}</td>
                <td className="dezent">
                  {z.freigabe === 'KUNDE' ? 'von Ihnen bestätigt'
                    : z.freigabe === 'BESTAND' ? (
                      <Tipp text="Dieser Name war vor der Umstellung bereits geladen. Das bedeutet noch nicht, dass er geprüft wurde." fokus>
                        <span>bereits geladen</span>
                      </Tipp>
                    ) : 'neu · bitte prüfen'}
                </td>
                <td className="r zahl dezent">{z.first_seen ? datumLang(z.first_seen) : '–'}</td>
                <td className="r zahl dezent">{z.last_seen ? datumLang(z.last_seen) : '–'}</td>
                <td>
                  <label>
                    <input type="checkbox" checked={z.freigabe === 'KUNDE'}
                           disabled={z.freigabe === 'KUNDE' || laeuft !== null}
                           onChange={() => bestaetigen(z.name)}
                           aria-label={`Rabattname ${z.name} bestätigen`} />
                    <span className="dezent" style={{ marginLeft: 6 }}>
                      {z.freigabe === 'KUNDE' ? 'bestätigt' : 'bestätigen'}
                    </span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Karte>
  )
}

function Freitextkarte({ zeilen, neuLaden }: {
  zeilen: EinrichtungArtikel[]
  neuLaden: () => void
}) {
  const [suche, setSuche] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const begriff = suche.trim().toLocaleLowerCase('de-DE')
  const sichtbar = (begriff
    ? zeilen.filter((z) => `${z.article_ref} ${z.name}`.toLocaleLowerCase('de-DE').includes(begriff))
    : zeilen.filter((z) => z.freitext)).slice(0, 100)
  const anzahlFreitext = zeilen.filter((z) => z.freitext).length
  const rolle: Record<string, string> = {
    KOPF: 'Zusammenstellung', ABFRAGE: 'Abfrage', UNTER: 'Unterartikel',
    NORMAL: 'Artikel', UNBEKANNT: 'nicht bekannt',
  }

  const setzen = (z: EinrichtungArtikel, freitext: boolean) => {
    setFehler(null); setLaeuft(z.article_ref)
    api.artikelFreitextSetzen(z.article_ref, freitext)
      .then(neuLaden)
      .catch((e) => setFehler(e.message))
      .finally(() => setLaeuft(null))
  }

  return (
    <Karte titel="Freitextfelder"
           neben={`${anzahl(anzahlFreitext)} von ${anzahl(zeilen.length)} Artikeln`}>
      {fehler && <div className="fehler" style={{ marginBottom: 10 }}>{fehler}</div>}
      <input className="artikelsuche" type="search" value={suche}
             onChange={(e) => setSuche(e.target.value)}
             placeholder="Artikel oder Nummer suchen"
             aria-label="Artikel suchen"
             style={{ width: 'min(100%, 360px)', marginBottom: 10 }} />
      {sichtbar.length ? (
        <table>
          <thead>
            <tr>
              <th>Artikel</th>
              <th>Nummer</th>
              <th>Rolle</th>
              <th>Freitext</th>
            </tr>
          </thead>
          <tbody>
            {sichtbar.map((z) => (
              <tr key={z.article_ref}>
                <td>{z.name}</td>
                <td className="zahl dezent">{z.article_ref}</td>
                <td className="dezent">{rolle[z.rolle] ?? z.rolle}</td>
                <td>
                  <label>
                    <input type="checkbox" checked={z.freitext}
                           disabled={laeuft === z.article_ref}
                           onChange={(e) => setzen(z, e.target.checked)} />
                    {z.freitext_quelle === 'KUNDE' && (
                      <span className="dezent" style={{ marginLeft: 6 }}>
                        festgelegt
                      </span>
                    )}
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="leer">
          {zeilen.length ? 'Kein passender Artikel.'
            : 'Noch keine Artikelliste eingelesen.'}
        </div>
      )}
      <p className="dezent" style={{ marginTop: 10 }}>
        Freitextfelder nehmen an der Kasse eine Notiz auf. Sie bleiben im
        Verlauf der Rechnung erhalten, stehen aber nicht in Rennern,
        Pennern oder Karteileichen. Eine Festlegung hier überschreibt der
        nächste Import nicht.
      </p>
    </Karte>
  )
}

function Stationenkarte({ zeilen, neuLaden }: {
  zeilen: EinrichtungStation[]
  neuLaden: () => void
}) {
  const [werte, setWerte] = useState<Record<string, string>>({})
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const schluessel = (z: EinrichtungStation) => `${z.location_id}:${z.terminal_ref}`
  const wert = (z: EinrichtungStation) => werte[schluessel(z)] ?? z.name ?? ''
  const speichern = async (z: EinrichtungStation) => {
    const k = schluessel(z); setLaeuft(k); setFehler(null)
    try {
      await api.stationBenennen(z.location_id, z.terminal_ref, wert(z).trim() || null)
      setWerte((alt) => { const neu = { ...alt }; delete neu[k]; return neu })
      neuLaden()
    } catch (e) { setFehler(e instanceof Error ? e.message : String(e)) }
    finally { setLaeuft(null) }
  }
  return (
    <Karte titel="Stationen" neben={`${anzahl(zeilen.length)} Kennungen`}>
      {fehler && <div className="fehler" style={{ marginBottom: 10 }}>{fehler}</div>}
      {zeilen.length ? <table><thead><tr><th>Standort</th><th>Kennung</th><th>Vorschlag</th><th>Erkennung</th><th>Name im Haus</th><th /></tr></thead>
        <tbody>{zeilen.map((z) => <tr key={schluessel(z)}>
          <td>{z.standort}</td>
          <td className="zahl dezent">{z.terminal_ref.length > 16 ? `${z.terminal_ref.slice(0, 8)}…` : z.terminal_ref}</td>
          <td>{z.abgeleiteter_name ? (
            <Tipp text={z.vorschlag_grund ?? 'Aus den Kassendaten vorgeschlagen'} fokus>
              <span>{z.abgeleiteter_name}</span>
            </Tipp>
          ) : <span className="dezent">kein sicherer Vorschlag</span>}</td>
          <td className="dezent">
            <span className="zahl">Ø {euro(z.positionswert)}</span>
            {' · '}{z.groesste_warengruppe ?? 'ohne Warengruppe'}
            {' · '}<span className="zahl">{euro(z.umsatz)} · {anzahl(z.positionen)} Pos.</span>
            <br />{z.first_seen ? datumLang(z.first_seen) : '–'} bis {z.last_seen ? datumLang(z.last_seen) : '–'}
          </td>
          <td><input value={wert(z)} placeholder={z.abgeleiteter_name ?? 'Name festlegen'}
            onChange={(e) => setWerte((alt) => ({ ...alt, [schluessel(z)]: e.target.value }))}
            aria-label={`Name der Station ${z.terminal_ref}`} /></td>
          <td className="r"><button disabled={laeuft !== null || wert(z) === (z.name ?? '')}
            onClick={() => void speichern(z)}>Speichern</button></td>
        </tr>)}</tbody></table> : <div className="leer">Noch keine Station aus einem Kassenarchiv gelesen.</div>}
      <p className="dezent" style={{ marginTop: 10 }}>
        Eindeutige Gerätenamen werden aus dem Transaktionsbericht vorgeschlagen.
        Was Sie hier festlegen, überschreibt der nächste Import nicht.
      </p>
    </Karte>
  )
}

const QUELLE: Record<string, string> = {
  ARCHIVE: 'DSFinV-K-Archiv',
  RECEIPTS: 'Quittungsreport',
  VOIDS: 'Stornoreport',
  ARTICLES: 'Artikelliste',
  PRELIM: 'Zwischenrechnung',
}

const ART: Record<string, string> = {
  FOOD: 'Speisen',
  BEVERAGE: 'Getränke',
  OTHER: 'Sonstiges',
  UNKNOWN: 'nicht zugeordnet',
}

function Zeile({ was, wert, fuss, gut }: {
  was: string; wert: string; fuss?: string; gut?: boolean
}) {
  return (
    <tr>
      <td style={{ width: 24 }} className="dezent">{gut === false ? '!' : '✓'}</td>
      <td><strong className="zahl">{wert}</strong> {was}</td>
      <td className="dezent" style={{ fontSize: 12 }}>{fuss}</td>
    </tr>
  )
}

function Hinweis({ h }: { h: EinrichtungHinweis }) {
  const [offen, setOffen] = useState(false)
  const marke = h.art === 'OFFEN' ? 'offen'
    : h.art === 'PRUEFEN' ? 'bitte ansehen' : 'zur Kenntnis'
  return (
    <div className={`punkt${h.art === 'OFFEN' ? ' offen' : ''}`}>
      <span className="marke">
        {marke}{h.anzahl > 0 && ` · ${anzahl(h.anzahl)}`}
      </span>
      {h.text}
      {h.artikel.length > 0 && (
        <>
          {' '}
          <button className="verweis" onClick={() => setOffen(!offen)}>
            {offen ? 'ausblenden' : 'anzeigen'}
          </button>
          {offen && (
            <ul>
              {h.artikel.map((a, i) => (
                <li key={`${a.ref}-${i}`}>
                  {a.ref && <span className="dezent zahl">{a.ref} </span>}
                  {a.name}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}

export function Einrichtung() {
  const [d, setD] = useState<EinrichtungBild | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  // Als benannte Funktion, weil die Zahlartenkarte sie nach dem
  // Festlegen erneut ruft: Die Art steht danach in der Tabelle, und
  // derselbe Punkt verschwindet aus „was noch zu klären ist".
  const laden = useCallback(() => {
    api.einrichtung().then(setD).catch((e) => setFehler(e.message))
  }, [])

  useEffect(() => { laden() }, [laden])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (!d) return <div className="laedt">Wird geladen …</div>

  const s = d.stammdaten
  const zugeordnet = d.warengruppen
    .filter((w) => w.art !== 'UNKNOWN')
    .reduce((a, w) => a + w.artikel, 0)
  const alle = d.warengruppen.reduce((a, w) => a + w.artikel, 0)
  // Eine Feststellung ist nichts, was jemand beantworten muss — sie
  // zählt nicht als offener Punkt.
  const zuKlaeren = d.offen.filter((h) => h.art !== 'HINWEIS').length

  return (
    <>
      <Karte titel="Quellen" neben="was eingelesen ist">
        <table>
          <thead>
            <tr>
              <th>Quelle</th>
              <th className="r">Dateien</th>
              <th className="r">Geschäftstage</th>
            </tr>
          </thead>
          <tbody>
            {(['ARCHIVE', 'RECEIPTS', 'VOIDS', 'ARTICLES'] as const).map((q) => {
              const z = d.quellen.find((x) => x.quelle === q)
              return (
                <tr key={q}>
                  <td>
                    <span className="dezent" style={{ marginRight: 8 }}>
                      {z ? '✓' : '–'}
                    </span>
                    {QUELLE[q] ?? q}
                  </td>
                  <td className="r zahl">{z ? anzahl(z.dateien) : '–'}</td>
                  <td className="r zahl dezent">
                    {q === 'ARTICLES' ? 'ohne Tagesbezug'
                      : z ? anzahl(z.tage) : '–'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Karte>

      <Zahlartenkarte zeilen={d.zahlarten ?? []} neuLaden={laden} />

      <Rabattartenkarte zeilen={d.rabattarten ?? []} neuLaden={laden} />

      <Stationenkarte zeilen={d.stationen ?? []} neuLaden={laden} />

      <Freitextkarte zeilen={d.artikel ?? []} neuLaden={laden} />

      <Karte titel="Standorte"
             neben={d.betriebe.length === 1 ? 'ein Haus'
               : `${anzahl(d.betriebe.length)} Häuser`}>
        {d.betriebe.length ? (
          <table>
            <thead>
              <tr>
                <th>Standort</th>
                <th>Kürzel</th>
                <th>Berichte</th>
                <th className="r">Dateien</th>
                <th className="r">bis</th>
              </tr>
            </thead>
            <tbody>
              {d.betriebe.map((b) => (
                <tr key={b.code}>
                  <td>
                    {b.name}
                    {b.herkunft === 'AUTOMATISCH' && !b.bestaetigt && (
                      <span className="merker" style={{ marginLeft: 7 }}>
                        beim Import angelegt
                      </span>
                    )}
                  </td>
                  <td className="dezent zahl">{b.code}</td>
                  <td className="dezent" style={{ fontSize: 11 }}>
                    {b.dateimuster
                      ? `„${b.dateimuster}“${b.muster_quelle === 'ORT'
                          ? ' — aus dem Ort der Betriebsstätte' : ''}`
                      : 'kein Muster — Berichte nur, wenn es der einzige aktive Standort ist'}
                  </td>
                  <td className="r zahl">{anzahl(b.dateien)}</td>
                  <td className="r zahl dezent">
                    {b.letzter_tag
                      ? new Date(b.letzter_tag).toLocaleDateString('de-DE')
                      : '–'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="leer">
            Noch keinen Standort. Er entsteht beim ersten Kassenarchiv —
            Name und Zuordnung kommen aus der Betriebsstätte im Archiv.
          </div>
        )}
        <p className="dezent" style={{
          fontSize: 11, marginTop: 12, marginBottom: 0, lineHeight: 1.6,
          maxWidth: '78ch',
        }}>
          Das Kassenarchiv findet seinen Standort selbst — es trägt die
          Kennung der Kasse. Die fünf Berichtsarten nicht: Quittungs-,
          Storno-, Transaktions- und Zahlungsbericht und der Tagesabschluss
          nennen den Standort nirgends im Inhalt, nur im Dateinamen. Dafür
          steht hier das Muster. Ohne Muster bleiben ihre Dateien liegen —
          außer es gibt genau einen aktiven Standort, dann gehen sie dorthin.
        </p>
      </Karte>

      <Karte titel="Was aus der Kasse gelesen wurde" neben={
        s ? `${s.filename} · ${new Date(s.gelesen_am).toLocaleDateString('de-DE')}`
          : undefined}>
        {s ? (
          <table>
            <tbody>
              <Zeile was="Artikel aus dem Backoffice" wert={anzahl(s.artikel)}
                     fuss={`${anzahl(s.warengruppen)} Warengruppen`} />
              <Zeile was="Zusammenstellungen erkannt" wert={anzahl(s.koepfe)}
                     fuss={`aus dem Typ „Combo-Menü“ — davon ${
                       anzahl(d.zerlegung.gebuchte_koepfe)} bisher verkauft`} />
              <Zeile was="Abfragen" wert={anzahl(s.abfragen)}
                     fuss={`${anzahl(s.pflichtabfragen)} davon mit Pflichtantwort`} />
              <Zeile was="Artikel nur über eine Abfrage erreichbar"
                     wert={anzahl(s.nur_ueber_abfrage)}
                     fuss="werden nie einzeln bestellt" />
              <Zeile was="Zuordnungen, woraus welche Zusammenstellung besteht"
                     wert={anzahl(d.bestandteile.kanten)}
                     gut={d.bestandteile.kanten > 0}
                     fuss={d.bestandteile.kanten
                       ? `${anzahl(d.bestandteile.koepfe)} Zusammenstellungen vollständig beschrieben`
                       : "fehlen — „Parent-SKU“ ist im Export leer"} />
              <Zeile was="der Artikel als Speise oder Getränk zugeordnet"
                     wert={alle ? prozent((zugeordnet / alle) * 100, 0) : '–'}
                     gut={s.ohne_art === 0 || zugeordnet / Math.max(alle, 1) > 0.95}
                     fuss="aus der Statistikgruppe der Kasse" />
            </tbody>
          </table>
        ) : (
          <div className="leer">
            Es ist noch keine Artikelliste eingelesen. Legen Sie den
            Artikelexport aus dem Kassen-Backoffice zu den anderen Dateien —
            dann steht die Einrichtung, bevor die erste Kassendatei kommt.
          </div>
        )}
      </Karte>

      <div className="gitter g-2" style={{ marginTop: 14 }}>
        <Karte titel="Speisen und Getränke" neben="je Warengruppe">
          <table>
            <thead>
              <tr>
                <th>Art</th>
                <th className="r">Warengruppen</th>
                <th className="r">Artikel</th>
              </tr>
            </thead>
            <tbody>
              {d.warengruppen.map((w) => (
                <tr key={w.art}>
                  <td>{ART[w.art] ?? w.art}</td>
                  <td className="r zahl">{anzahl(w.gruppen)}</td>
                  <td className="r zahl dezent">{anzahl(w.artikel)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Karte>

        <Karte titel="Beistellungen" neben="was auf eine Abfrage antwortet">
          {d.bestandteile.kanten > 0 ? (
            <>
              <table>
                <tbody>
                  <tr>
                    <td>Abfragen mit Antworten</td>
                    <td className="r zahl">{anzahl(d.bestandteile.abfragen)}</td>
                  </tr>
                  <tr>
                    <td>Zuordnungen Artikel → Zusammenstellung</td>
                    <td className="r zahl">{anzahl(d.bestandteile.zuordnungen)}</td>
                  </tr>
                </tbody>
              </table>
              <p className="merker" style={{ marginTop: 12 }}>
                Woraus jedes Gericht besteht, steht in den Stammdaten —
                Beilage, Dip und Größenwahl müssen nicht mehr aus der Lage
                auf dem Bon hergeleitet werden.
              </p>
            </>
          ) : d.beistellungsgruppen.length ? (
            <>
              <table>
                <tbody>
                  {d.beistellungsgruppen.map((g) => (
                    <tr key={g.name}>
                      <td>{g.name}</td>
                      <td className="r dezent" style={{ fontSize: 12 }}>
                        {g.quelle === 'KUNDE' ? 'von Ihnen festgelegt'
                          : g.quelle === 'STAMMDATEN' ? 'aus den Stammdaten'
                            : 'aus den Buchungen hergeleitet'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="merker" style={{ marginTop: 12 }}>
                Ob eine Beilage zum Gericht gehört oder einzeln bestellt
                wurde, steht in den Stammdaten nicht: Die Größenwahl liegt
                dort genauso hinter „Sperren“ wie die Beilage. Was beide
                trennt, ist die Stelle auf dem Bon — deshalb wird das
                hergeleitet und nicht gelesen.
              </p>
            </>
          ) : (
            <div className="leer">Keine Gruppe gilt als Beistellung.</div>
          )}
        </Karte>
      </div>

      <Karte titel="Was noch zu klären ist"
             neben={zuKlaeren
               ? `${anzahl(zuKlaeren)} ${zuKlaeren === 1 ? 'Punkt' : 'Punkte'}`
               : 'nichts offen'}>
        {d.offen.length ? d.offen.map((h, i) => <Hinweis key={i} h={h} />)
          : <div className="leer">Nichts offen.</div>}
      </Karte>
    </>
  )
}
